import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { View } from '@olxbr/design-system'

import ChangePasswordBaseTemplate from '../ChangePasswordBaseTemplate'
import { GenericErrorBox } from '../../molecules/GenericErrorBox'
import Lurker from '../../../util/lurker'
import Subtitle from '../../atoms/Subtitle'

import * as apiCall from '../../../api/accountReactivationApiCall'

const lurker = new Lurker('account_reactivation_change_password')

// prettier-ignore
const ErrorMsgWrapper = styled(View)`
  margin-top: 16px;
  margin-right: 0;
  margin-bottom: 16px;
  margin-left: 0;
`

const getPasswordExtraData = (key, password) => {
  const field = {}
  const passwordLevel = password.level

  if (passwordLevel === -1) {
    field[key] = 'sequential'
  } else if (passwordLevel === 1 || passwordLevel === 2) {
    field[key] = 'weak'
  } else if (passwordLevel === 3) {
    field[key] = 'good'
  } else if (passwordLevel === 4) {
    field[key] = 'strong'
  }
  return field
}

const errors = {
  showInvalidTokenError: false,
  showAccountNotFoundError: false,
  showAccountNotInactiveError: false,
  showSamePasswordError: false,
  showInvalidCredentialsError: false,
  showGenericError: false,
  showRateLimitError: false,
  showPasswordNotAcceptedError: false,
}

const Description = styled(View)`
  display: flex;
  flex-direction: column;
  padding-top: 16px;
  padding-bottom: 24px;
`

const renderDescription = () => (
  <Description>
    <Subtitle>Para proteger sua conta, crie uma nova senha.</Subtitle>
    <Subtitle>Quanto mais forte, mais segura.</Subtitle>
  </Description>
)

class AccountReactivationChangePasswordTemplate extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      ...errors,
    }
  }

  async componentDidMount() {
    lurker.pageLoadEvt()
  }

  onGenericError = (error = {}) => {
    const {
      response: { status, data: { message } = {} } = {},
      message: exceptionMessage = 'unknown',
    } = error

    if (this.props.onError) {
      this.props.onError()
    }

    if (status === 429) return this.handle429()
    if (status === 422) return this.handle422(message)
    if (status) return this.handleError(status, message)
    return this.handleUnexpectedError(exceptionMessage)
  }

  handle429 = () => {
    this.handleExpectedStatusCode(429, 'rate_limit_exceeded', 'showRateLimitError')
  }

  handle422 = message => {
    if (message === 'SAME_PASSWORD')
      return this.handleExpectedStatusCode(422, message.toLowerCase(), 'showSamePasswordError')
    this.handleExpectedStatusCode(422, 'password_not_accepted', 'showPasswordNotAcceptedError')
  }

  handleError = (status, message) => {
    switch (message) {
      case 'ACCOUNT_NOT_FOUND':
        this.handleExpectedStatusCode(status, message.toLowerCase(), 'showAccountNotFoundError')
        break
      case 'ACCOUNT_NOT_INACTIVE':
        this.handleExpectedStatusCode(status, message.toLowerCase(), 'showAccountNotInactiveError')
        break
      case 'INVALID_CREDENTIALS':
        this.handleExpectedStatusCode(status, message.toLowerCase(), 'showInvalidCredentialsError')
        break
      case 'INVALID_TOKEN':
        this.handleExpectedStatusCode(status, message.toLowerCase(), 'showInvalidTokenError')
        break
      default:
        this.handleUnexpectedStatusCode(status, message)
    }
  }

  handleExpectedStatusCode = (status, message, error) => {
    lurker.viewMessageEvt('error', {
      accounts_reason: message,
      accounts_status_code: status,
    })
    this.showError(error)
  }

  handleUnexpectedStatusCode = (status, message = 'unknown') => {
    lurker.viewMessageEvt('error', {
      accounts_reason: 'unexpected_status_code',
      accounts_status_code: status,
      accounts_error_message: message.toLowerCase(),
    })
    this.showError('showGenericError')
  }

  handleUnexpectedError = (message = 'unknown') => {
    lurker.viewMessageEvt('error', {
      accounts_reason: 'unexpected_error',
      accounts_error_message: message,
    })
    this.showError('showGenericError')
  }

  showError = error => {
    this.setState({
      ...errors,
      [error]: true,
    })
  }

  handleSubmit = ({ password }) => {
    const { token, onSuccess, abTests } = this.props
    this.setState({ ...errors })
    if (this.props.onSubmit) {
      this.props.onSubmit()
    }
    return apiCall
      .reactivateAccountStrongPassword(token, password.value)
      .then(async data => {
        lurker.viewMessageEvt('success', {
          ...getPasswordExtraData('password_strength', password),
        })

        if (onSuccess) {
          onSuccess(data)
        }
      })
      .catch(this.onGenericError)
  }

  renderError = () => {
    const { email, onReactivateClick, onRegisterClick, onLoginClick } = this.props
    const {
      showInvalidTokenError,
      showAccountNotFoundError,
      showAccountNotInactiveError,
      showSamePasswordError,
      showInvalidCredentialsError,
      showGenericError,
      showRateLimitError,
      showPasswordNotAcceptedError,
    } = this.state
    return (
      <React.Fragment>
        {showInvalidTokenError && (
          <ErrorMsgWrapper>
            <GenericErrorBox
              errorAction={() => onReactivateClick(email)}
              text="Sua sessão expirou."
              info="Quer reativar sua conta? "
              linkText="Tente novamente."
            />
          </ErrorMsgWrapper>
        )}
        {showAccountNotFoundError && (
          <ErrorMsgWrapper>
            <GenericErrorBox
              showAccountNotFoundError
              errorAction={onRegisterClick}
              text="Não existe uma conta com este e-mail."
            />
          </ErrorMsgWrapper>
        )}
        {showAccountNotInactiveError && (
          <ErrorMsgWrapper>
            <GenericErrorBox
              errorAction={onLoginClick}
              text="Essa conta já está ativa."
              linkText="Acesse a sua conta."
            />
          </ErrorMsgWrapper>
        )}
        {showSamePasswordError && (
          <ErrorMsgWrapper>
            <GenericErrorBox text="Digite uma senha diferente da senha atual." />
          </ErrorMsgWrapper>
        )}
        {showInvalidCredentialsError && (
          <ErrorMsgWrapper>
            <GenericErrorBox text="Não foi possível acessar sua conta." />
          </ErrorMsgWrapper>
        )}
        {showGenericError && (
          <ErrorMsgWrapper>
            <GenericErrorBox text="Não foi possível salvar sua nova senha." />
          </ErrorMsgWrapper>
        )}
        {showRateLimitError && (
          <ErrorMsgWrapper>
            <GenericErrorBox
              showContactUs={false}
              text="Você excedeu o número de tentativas. Por favor, tente novamente mais tarde."
            />
          </ErrorMsgWrapper>
        )}
        {showPasswordNotAcceptedError && (
          <ErrorMsgWrapper>
            <GenericErrorBox
              showContactUs={false}
              text="Esta senha não atende aos requisitos mínimos."
            />
          </ErrorMsgWrapper>
        )}
      </React.Fragment>
    )
  }

  render() {
    const { showPasswodConfirmationInput, abTests } = this.props
    return (
      <ChangePasswordBaseTemplate
        buttonText="Salvar nova senha"
        lurker={lurker}
        onSubmit={this.handleSubmit}
        renderError={this.renderError}
        renderTitle={renderDescription}
        showPasswodConfirmationInput={showPasswodConfirmationInput}
        abTests={abTests}
      />
    )
  }
}

AccountReactivationChangePasswordTemplate.defaultProps = {
  showPasswodConfirmationInput: true,
}

AccountReactivationChangePasswordTemplate.propTypes = {
  email: PropTypes.string,
  onError: PropTypes.func,
  onLoginClick: PropTypes.func.isRequired,
  onReactivateClick: PropTypes.func.isRequired,
  onRegisterClick: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  onSuccess: PropTypes.func,
  showPasswodConfirmationInput: PropTypes.bool,
  token: PropTypes.string,
  abTests: PropTypes.string,
}

export default AccountReactivationChangePasswordTemplate
